import React, { useMemo, useState, useCallback, useContext, useLayoutEffect } from 'react'
import { Link, useHistory } from 'react-router-dom'
import Modal from '@mui/material/Modal'
import { PurchaseBNZ } from '../../components'
import DashboardContext from '../../contexts/DashboardContext'
import { decodedToken } from '../../services/auth'
import {
  FileText,
  ArrowRight,
  Video,
  Sparkles,
  Clock,
  CheckCircle2,
  AlertCircle,
  Wallet,
  Plus,
  ArrowDownLeft,
  ArrowUpRight,
} from 'lucide-react'
import CustomSpinner from '../CustomSpinner'
import RequestService from '../Modal/BonuzPlans/ResquestService'
import { formatISOTimestampToDate } from '../../utils/formatDate'
import './dashboard-bonuz-essencial-home.css'

const REVISIA_URL = 'https://revisia.bonuz.it/auth/bonuz/redirect'

function demandStatusLabel(status) {
  const map = {
    pending: 'Pendente',
    in_progress: 'Em andamento',
    stand_by: 'Stand-by',
    waiting_approval: 'Aguardando aprovação',
    in_estimating: 'Aguardando orçamento',
    draft: 'Pendente de informação',
    demand_sent: 'Demanda enviada',
    in_review: 'Aguardando revisão',
    canceled: 'Cancelado',
    done: 'Concluído',
    waiting_estimate_approval: 'Aguardando aprovação do orçamento',
    waiting_lawyer_acceptance: 'Aguardando aceite do advogado',
  }
  return map[status] || 'Em andamento'
}

function statusIconClass(status) {
  switch (status) {
    case 'done':
    case 'demand_sent':
      return 'green'
    case 'pending':
    case 'draft':
    case 'waiting_approval':
    case 'in_review':
    case 'in_estimating':
    case 'waiting_estimate_approval':
    case 'waiting_lawyer_acceptance':
      return 'amber'
    case 'in_progress':
      return 'blue'
    default:
      return 'gray'
  }
}

function StatusGlyph({ status }) {
  const cls = statusIconClass(status)
  if (status === 'done' || status === 'demand_sent') {
    return <CheckCircle2 size={16} />
  }
  if (cls === 'amber') {
    return <AlertCircle size={16} />
  }
  if (cls === 'blue') {
    return <Clock size={16} />
  }
  return <Clock size={16} />
}

function formatDemandLineDate(demand) {
  if (demand?.deadline) {
    try {
      const { formattedDate } = formatISOTimestampToDate(demand.deadline, 'ISO')
      return `Prazo ${formattedDate}`
    } catch (e) {
      return ''
    }
  }
  if (demand?.finalDate) {
    try {
      const { formattedDate } = formatISOTimestampToDate(demand.finalDate, 'ISO')
      return formattedDate
    } catch (e) {
      return ''
    }
  }
  return ''
}

const DashboardBonuzEssencialHome = ({
  wallet,
  lastStatements,
  loadingStatements,
  recentDemandsData,
  recentDemandsLoading,
  totalDemandsCount,
  onPurchaseModalClose,
}) => {
  const history = useHistory()
  const [, setPackageId] = useContext(DashboardContext)
  const [purchaseModalOpen, setPurchaseModalOpen] = useState(false)
  const [purchaseUnavailable, setPurchaseUnavailable] = useState(false)

  useLayoutEffect(() => {
    const jwtSettings = decodedToken()
    if (jwtSettings?.adminId && jwtSettings?.adminAsUser) {
      setPurchaseUnavailable(true)
    }
  }, [])

  const closePurchaseModal = useCallback(() => {
    setPurchaseModalOpen(false)
    setPackageId('false')
    if (onPurchaseModalClose) {
      onPurchaseModalClose()
    }
  }, [setPackageId, onPurchaseModalClose])

  const openPurchaseModal = useCallback(() => {
    if (!purchaseUnavailable) {
      setPurchaseModalOpen(true)
    }
  }, [purchaseUnavailable])

  /* Mesmo fluxo dos botões "Upgrade" em /dashboard/para-empresas/planos (RequestService) */
  const [openModalRequestService, setOpenModalRequestService] = useState(false)
  const modalOpenRequestService = useCallback(() => {
    setOpenModalRequestService((v) => !v)
  }, [])

  const essencial = wallet?.creditsBonuzPlans ?? 0
  /* Mesmo critério do rodapé (CreditsBalloon): após merge em DashboardRoutes, balance vira total; adquiridos = saldoFinal */
  const adquiridos = wallet?.saldoFinal ?? wallet?.balance ?? 0

  const demandRows = useMemo(() => {
    const raw = recentDemandsData?.listDemands?.data || []
    const active = raw.filter((d) => d.status !== 'done' && d.status !== 'canceled')
    return active.slice(0, 5)
  }, [recentDemandsData])

  const statements = lastStatements || []

  return (
    <div className="dbe-root">
      <Modal
        open={purchaseModalOpen}
        onClose={closePurchaseModal}
        disableAutoFocus
        sx={{
          zIndex: '9999',
          '& .MuiBackdrop-root': {
            backgroundColor: 'var(--overlay-bg)',
            transition: 'background-color 0.3s ease',
          },
        }}
      >
        <PurchaseBNZ closeParentModal={closePurchaseModal} />
      </Modal>
      <RequestService
        openModal={openModalRequestService}
        handleClose={modalOpenRequestService}
        requestedService={false}
      />
      <div className="dbe-stack">
        <section className="dbe-hero">
          <div className="dbe-hero-blur" aria-hidden />
          <div className="dbe-hero-inner">
            <h2>Upgrade para o Plano Premium</h2>
            <p>
              Tenha um Legal Manager dedicado para atender suas demandas diretamente com SLA de apenas 2 dias úteis.
            </p>
            <div className="dbe-hero-cta">
              <button type="button" className="dbe-btn-primary" onClick={modalOpenRequestService}>
                Upgrade para Premium
              </button>
              <button type="button" className="dbe-btn-secondary" onClick={modalOpenRequestService}>
                Contratar Data Privacy com DPO
              </button>
            </div>
          </div>
        </section>

        <div className="dbe-grid-main">
          <div className="dbe-col-wide dbe-stack">
            <div className="dbe-card">
              <div className="dbe-card-pad">
                <h3 className="dbe-card-title">Consulte e solicite um serviço agora mesmo</h3>
                <p className="dbe-card-muted">Nossa equipe jurídica está pronta para atender suas demandas</p>
                <Link to="/dashboard/serviços/solicitar" className="dbe-btn-solid">
                  <FileText size={18} />
                  Solicitar serviço
                  <ArrowRight size={18} />
                </Link>
              </div>
            </div>

            <div className="dbe-quick-grid">
              <div className="dbe-quick-card">
                <div className="dbe-quick-row">
                  <div className="dbe-icon-box">
                    <Video size={24} />
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <h3>Fale com advogado</h3>
                    <p>Consultoria de 30 min por videochamada</p>
                    <Link to="/dashboard/fala-advogado" className="dbe-btn-outline">
                      Agendar consulta
                    </Link>
                  </div>
                </div>
              </div>
              <div className="dbe-quick-card accent-border">
                <div className="dbe-quick-row">
                  <div className="dbe-icon-box filled">
                    <Sparkles size={24} />
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div className="dbe-quick-head">
                      <h3 style={{ marginBottom: 0 }}>Revisia</h3>
                      <span className="dbe-badge-new">Novo!</span>
                    </div>
                    <p>Análise inteligente de documentos com IA</p>
                    <a href={REVISIA_URL} target="_blank" rel="noopener noreferrer" className="dbe-btn-solid">
                      Experimentar agora
                    </a>
                  </div>
                </div>
              </div>
            </div>

            <div className="dbe-card">
              <div className="dbe-demands-header">
                <h3>Demandas ativas</h3>
                <span className="dbe-badge-count">{totalDemandsCount} total</span>
              </div>
              {recentDemandsLoading && (
                <div className="dbe-spinner-wrap">
                  <CustomSpinner />
                </div>
              )}
              {!recentDemandsLoading && demandRows.length === 0 && (
                <div className="dbe-empty">Você ainda não possui demandas ativas.</div>
              )}
              {!recentDemandsLoading &&
                demandRows.map((demand) => {
                  const st = statusIconClass(demand.status)
                  return (
                    <div
                      key={demand._id}
                      className="dbe-demand-row"
                      role="button"
                      tabIndex={0}
                      onClick={() => history.push(`/dashboard/serviços/detalhes/${demand._id}`)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') history.push(`/dashboard/serviços/detalhes/${demand._id}`)
                      }}
                    >
                      <div className="dbe-demand-left">
                        <div className={`dbe-status-icon ${st}`}>
                          <StatusGlyph status={demand.status} />
                        </div>
                        <div style={{ minWidth: 0 }}>
                          <p className="dbe-demand-title" title={demand.fullDescription || demand.code}>
                            {(demand.fullDescription || demand.code || '').slice(0, 80)}
                            {(demand.fullDescription || '').length > 80 ? '…' : ''}
                          </p>
                          <div className="dbe-demand-meta">
                            <span className="dbe-chip">{demandStatusLabel(demand.status)}</span>
                            {formatDemandLineDate(demand) && (
                              <span className="dbe-demand-date">{formatDemandLineDate(demand)}</span>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="dbe-demand-credits">
                        <strong>{demand.price != null ? demand.price : '—'}</strong>
                        <span>créditos</span>
                      </div>
                    </div>
                  )
                })}
            </div>
          </div>

          <div className="dbe-stack">
            <div className="dbe-credit-card">
              <div className="dbe-credit-inner">
                <div className="dbe-credit-top">
                  <div className="dbe-credit-label">
                    <Wallet size={16} />
                    <span>Meus créditos</span>
                  </div>
                  <button
                    type="button"
                    className="dbe-credit-buy"
                    onClick={openPurchaseModal}
                    disabled={purchaseUnavailable}
                  >
                    <Plus size={16} />
                    Comprar
                  </button>
                </div>
                <div className="dbe-credit-block">
                  <div className="lbl">Essencial</div>
                  <div className="dbe-credit-figures">
                    <span className="val">{essencial}</span>
                    <span className="val-unit">BNZs</span>
                  </div>
                  <div className="lbl2">*seu saldo de BNZs disponíveis</div>
                </div>
                <div className="dbe-sep" />
                <div className="dbe-credit-block">
                  <div className="lbl">Adquiridos</div>
                  <div className="dbe-credit-figures">
                    <span className="val">{adquiridos}</span>
                    <span className="val-unit">BNZs</span>
                  </div>
                  <div className="lbl2">*créditos adicionais em carteira</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="dbe-card">
          <div className="dbe-trans-header">
            <h3>Transações recentes</h3>
          </div>
          {loadingStatements && (
            <div className="dbe-spinner-wrap">
              <CustomSpinner />
            </div>
          )}
          {!loadingStatements && (!statements || statements.length === 0) && (
            <div className="dbe-empty">Você não fez nenhuma transação recente.</div>
          )}
          {!loadingStatements &&
            statements.map((item) => {
              const isCredit =
                item.type === 'credit' ||
                (item.type !== 'debit' && item.type !== 'reserve' && parseFloat(item.amount) > 0)
              const amt = Math.abs(parseFloat(item.amount))
              const { formattedDate, formattedHourMinute } = formatISOTimestampToDate(item.createdAt, 'ISO')
              return (
                <div key={item._id} className="dbe-trans-row">
                  <div className="dbe-trans-left">
                    <div className={`dbe-trans-ico ${isCredit ? 'credit' : 'debit'}`}>
                      {isCredit ? <ArrowDownLeft size={16} /> : <ArrowUpRight size={16} />}
                    </div>
                    <div>
                      <p className="dbe-trans-title">{item.description}</p>
                      <p className="dbe-trans-sub">
                        {formattedDate} {formattedHourMinute} • Transação em BNZs
                      </p>
                    </div>
                  </div>
                  <p className={`dbe-trans-amt ${isCredit ? 'credit' : 'debit'}`}>
                    {isCredit ? '+' : '-'}
                    {amt.toLocaleString('pt-BR')} BNZs
                  </p>
                </div>
              )
            })}
        </div>
      </div>
    </div>
  )
}

export default DashboardBonuzEssencialHome
